import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, X, ChevronLeft, ChevronRight, ChevronDown, ChevronUp } from 'lucide-react';

const TreasuredResistanceProject = () => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [statementOpen, setStatementOpen] = useState(false);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, []);

  const galleryImages = [
    'https://customer-assets.emergentagent.com/job_amy-documentary/artifacts/woqof6q7_000000020013.jpg',
    'https://customer-assets.emergentagent.com/job_amy-documentary/artifacts/kuoomjcc_000000050006-2.jpg',
    'https://customer-assets.emergentagent.com/job_amy-documentary/artifacts/j02hu6gp_000000020003-1.jpg',
    'https://customer-assets.emergentagent.com/job_amy-documentary/artifacts/na33xqbp_000000100011-5.jpg',
    'https://customer-assets.emergentagent.com/job_amy-documentary/artifacts/vb91ibyx_000000100014-2.jpg',
    'https://customer-assets.emergentagent.com/job_amy-documentary/artifacts/hd4pozxt_1_000000100005.jpg',
    'https://customer-assets.emergentagent.com/job_amy-documentary/artifacts/foyymcjn_000000020014.jpg',
    'https://customer-assets.emergentagent.com/job_amy-documentary/artifacts/czjpzep2_000000050014.jpg',
    'https://customer-assets.emergentagent.com/job_amy-documentary/artifacts/yj62c3z9_000000110007-7.jpg',
  ];

  const openLightbox = (index) => {
    setCurrentImageIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => setLightboxOpen(false);

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % galleryImages.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
  };

  return (
    <div className="bg-cream min-h-screen">
      {/* Back Button */}
      <div className="fixed top-24 left-6 md:left-12 z-40">
        <Link 
          to="/#photography" 
          className="inline-flex items-center gap-2 text-green hover:text-green/70 transition-colors"
        >
          <ArrowLeft size={20} />
          <span className="text-sm uppercase tracking-wider">Back</span>
        </Link>
      </div>

      {/* Title */}
      <section className="pt-32 pb-8 px-6 md:px-12">
        <div className="max-w-5xl mx-auto text-center">
          <h1 
            className="text-6xl md:text-8xl lg:text-9xl uppercase text-green mb-4"
            style={{ fontFamily: 'Anton, sans-serif' }}
          >
            Waste Workers of Bahia
          </h1>
          <p className="text-xl md:text-2xl text-green/70">
            Bahia, Brazil · 2025
          </p>
        </div>
      </section>

      {/* Artist Statement Toggle */}
      <section className="px-6 md:px-12 pb-8">
        <div className="max-w-4xl mx-auto">
          <button
            onClick={() => setStatementOpen(!statementOpen)}
            className="flex items-center gap-2 text-green hover:text-green/70 transition-colors mx-auto"
          >
            <span className="text-sm uppercase tracking-wider">Artist Statement</span>
            {statementOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </button>
          
          {statementOpen && (
            <div className="mt-8 space-y-6 text-green/90" style={{ fontFamily: "'DM Sans', sans-serif" }}>
              <p className="text-lg leading-relaxed">
                Photographed in 2025 in Bahia, Brazil, this series follows the Grupo Ambientalistas de Palmeiras (GAP), activists who transform discarded plastic into wearable protest.
              </p>
              <p className="text-lg leading-relaxed">
                Working atop an abandoned diamond mine, they sort through recycling materials and handcraft them into armour and costumes for carnival performances.
              </p>
              <p className="text-lg leading-relaxed">
                In a country among the world's largest plastic polluters, the project examines environmental collapse as a lived reality, where waste is unavoidable and resistance emerges through creativity.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Image Gallery Grid - Uniform squares like Laura Pannack */}
      <section className="px-6 md:px-12 lg:px-24 pb-24">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
            {galleryImages.map((image, idx) => (
              <div 
                key={idx} 
                className="aspect-square cursor-pointer overflow-hidden"
                onClick={() => openLightbox(idx)}
              >
                <img 
                  src={image} 
                  alt={`Waste Workers of Bahia ${idx + 1}`}
                  className="w-full h-full object-cover hover:opacity-90 transition-opacity"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {lightboxOpen && (
        <div className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center">
          <button 
            onClick={closeLightbox}
            className="absolute top-6 right-6 text-white hover:text-white/70 transition-colors"
          >
            <X size={32} />
          </button>
          <button 
            onClick={prevImage}
            className="absolute left-6 text-white hover:text-white/70 transition-colors"
          >
            <ChevronLeft size={48} />
          </button>
          <img 
            src={galleryImages[currentImageIndex]} 
            alt={`Gallery ${currentImageIndex + 1}`}
            className="max-h-[90vh] max-w-[90vw] object-contain"
          />
          <button 
            onClick={nextImage}
            className="absolute right-6 text-white hover:text-white/70 transition-colors"
          >
            <ChevronRight size={48} />
          </button>
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/70 text-sm">
            {currentImageIndex + 1} / {galleryImages.length}
          </div>
        </div>
      )}
    </div>
  );
};

export default TreasuredResistanceProject;
